import React from "react";
import { motion } from "framer-motion";
import {
  Headset,
  ShieldCheck,
  Server,
  DatabaseBackup,
  KeyRound,
  Network,
  ArrowUpRight,
  Code2,
  Sparkles,
  Zap,
} from "lucide-react";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { SERVICE_KEYS, buildWhatsAppLink } from "../lib/constants";

const ICONS = {
  support: Headset,
  security: ShieldCheck,
  infra: Server,
  backup: DatabaseBackup,
  licensing: KeyRound,
  projects: Network,
};

const ServiceCard = ({ k, index }) => {
  const { t } = useLang();
  const item = t.services.items[k];
  const Icon = ICONS[k];

  return (
    <Reveal
      delay={(index % 3) * 0.08}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0e] p-8 md:p-10 transition-colors duration-500 hover:border-[#0047ff]/50"
      data-testid={`service-card-${k}`}
    >
      {/* glow on hover */}
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 blue-glow opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      <div className="relative flex items-start justify-between">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] transition-[transform,border-color,background-color] duration-500 group-hover:-translate-y-1 group-hover:border-[#00e5ff]/40 group-hover:bg-[#00e5ff]/5">
          <Icon className="h-6 w-6 text-[#00e5ff] transition-transform duration-500 group-hover:scale-110" />
        </div>
        <span className="font-mono-wt text-sm text-zinc-600 transition-colors duration-500 group-hover:text-[#0047ff]">
          0{index + 1}
        </span>
      </div>

      <h3 className="relative mt-8 font-display text-xl md:text-2xl font-medium text-white">
        {item.title}
      </h3>
      <p className="relative mt-3 text-sm md:text-base font-light leading-relaxed text-zinc-400">
        {item.short}
      </p>

      {/* Expanding detail */}
      <div className="relative grid grid-rows-[0fr] transition-[grid-template-rows] duration-500 ease-out group-hover:grid-rows-[1fr]">
        <div className="overflow-hidden">
          <p className="pt-4 text-sm font-light leading-relaxed text-zinc-500">
            {item.long}
          </p>
        </div>
      </div>

      <div className="relative mt-6 flex flex-wrap gap-2">
        {item.tags.map((tag) => (
          <span
            key={tag}
            className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-400"
          >
            {tag}
          </span>
        ))}
      </div>

      <a
        data-testid={`service-more-${k}`}
        href={buildWhatsAppLink(t.services.whatsappMsg(item.title))}
        target="_blank"
        rel="noopener noreferrer"
        className="relative mt-7 inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 hover:text-[#00e5ff] transition-colors duration-300"
      >
        {t.services.more}
        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </a>
    </Reveal>
  );
};

export const Services = () => {
  const { t } = useLang();
  return (
    <section
      id="servicos"
      data-testid="services-section"
      className="relative py-24 md:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <span className="font-mono-wt text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#0047ff]">
              {t.services.overline}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
              {t.services.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-base md:text-lg font-light text-zinc-400">
              {t.services.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-6 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-white/10 bg-white/[0.02] px-5 py-2.5">
              <span className="font-mono-wt text-xs sm:text-sm font-semibold tracking-wide text-white">
                {t.services.tagline}
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICE_KEYS.map((k, i) => (
            <ServiceCard key={k} k={k} index={i} />
          ))}
        </div>

        <DevelopmentFeature />
      </div>
    </section>
  );
};

const DevelopmentFeature = () => {
  const { t } = useLang();
  const d = t.services.development;
  const codeLines = [
    { t: "const waltech", c: "text-[#00e5ff]" },
    { t: "  .build(", c: "text-zinc-500" },
    { t: "    'sistema'", c: "text-[#7aa2ff]" },
    { t: "  )", c: "text-zinc-500" },
    { t: "  .deploy() ✓", c: "text-emerald-400" },
  ];

  return (
    <Reveal
      delay={0.1}
      data-testid="service-card-development"
      className="group relative mt-6 overflow-hidden rounded-2xl border border-[#0047ff]/40 bg-[#0a0a0e] transition-colors duration-500 hover:border-[#00e5ff]/50"
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 blue-glow opacity-60 transition-opacity duration-500 group-hover:opacity-100" />
      <div className="absolute inset-0 grid-lines opacity-20" />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 md:p-12">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[#00e5ff]/40 bg-[#00e5ff]/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#00e5ff] font-mono-wt">
              <Sparkles className="h-3.5 w-3.5" />
              {d.overline}
            </span>
            <span className="font-mono-wt text-sm text-zinc-600">07</span>
          </div>

          <div className="mt-6 flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] transition-[transform,border-color] duration-500 group-hover:-translate-y-1 group-hover:border-[#00e5ff]/40">
              <Code2 className="h-6 w-6 text-[#00e5ff]" />
            </div>
            <h3 className="font-display text-2xl md:text-3xl font-medium text-white">
              {d.title}
            </h3>
          </div>

          <p className="mt-5 max-w-xl text-base font-light leading-relaxed text-zinc-300">
            {d.desc}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {d.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-white/10 px-3 py-1 text-xs text-zinc-400 transition-colors duration-300 hover:border-[#00e5ff]/40 hover:text-white"
              >
                {tag}
              </span>
            ))}
          </div>

          <a
            data-testid="service-development-cta"
            href={buildWhatsAppLink(d.whatsappMsg)}
            target="_blank"
            rel="noopener noreferrer"
            className="group/btn mt-8 inline-flex items-center gap-2 rounded-full bg-[#0047ff] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#2b5bff] transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_28px_rgba(0,71,255,0.5)]"
          >
            {d.cta}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </a>
        </div>

        {/* Right: code mock */}
        <div className="lg:col-span-5">
          <div className="relative h-full overflow-hidden rounded-xl border border-white/10 bg-[#050507]/80 p-5">
            <div className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <span className="ml-2 font-mono-wt text-[11px] text-zinc-600">app.js</span>
            </div>
            <pre className="mt-4 font-mono-wt text-sm leading-7">
              {codeLines.map((l, i) => (
                <div key={i} className="flex gap-4">
                  <span className="select-none text-zinc-700">{i + 1}</span>
                  <code className={l.c}>{l.t}</code>
                </div>
              ))}
            </pre>
            <div className="mt-5 flex items-center gap-3 border-t border-white/5 pt-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#00e5ff]/10">
                <Zap className="h-4 w-4 text-[#00e5ff]" />
              </span>
              <div>
                <div className="text-sm font-semibold text-white">Automação & Integração</div>
                <div className="text-[11px] text-zinc-500">APIs · Web · Automação de processos</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
};
